import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { TabItem, TabItemId, TabItems } from '@sg/garnish';

import { ordersTabsMessages as messages } from './useOrdersTabs.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrdersTabs = (params: UseOrdersTabsParams) => {
  const { tabIdParam, isInStoreOrdersEnabled } = params;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const ordersTab = useMemo<TabItem>(
    () => ({
      id: 'orders',
      panelId: 'orders.orders-tab-panel',
      label: formatMessage(messages.ordersTabLabel),
      'aria-label': formatMessage(messages.ordersTabA11yLabel),
    }),
    [formatMessage],
  );

  const giftCardsTab = useMemo<TabItem>(
    () => ({
      id: 'gift-cards',
      panelId: 'orders.gift-cards-tab-panel',
      label: formatMessage(messages.giftCardsTabLabel),
      'aria-label': formatMessage(messages.giftCardsTabA11yLabel),
    }),
    [formatMessage],
  );

  const digitalOrdersTab = useMemo<TabItem>(
    () => ({
      id: 'digital-orders',
      panelId: 'orders.digital-orders-tab-panel',
      label: formatMessage(messages.digitalOrdersTabLabel),
      'aria-label': formatMessage(messages.digitalOrdersTabA11yLabel),
    }),
    [formatMessage],
  );

  const inStoreOrdersTab = useMemo<TabItem>(
    () => ({
      id: 'in-store-orders',
      panelId: 'orders.in-store-orders-tab-panel',
      label: formatMessage(messages.inStoreOrdersTabLabel),
      'aria-label': formatMessage(messages.inStoreOrdersTabA11yLabel),
    }),
    [formatMessage],
  );

  const firstTab = isInStoreOrdersEnabled ? digitalOrdersTab : ordersTab;
  const secondTab = isInStoreOrdersEnabled ? inStoreOrdersTab : giftCardsTab;

  const tabs = useMemo<TabItems>(() => [firstTab, secondTab], [firstTab, secondTab]);

  const validTabIdParam = new Set([firstTab.id, secondTab.id]);

  const [activeTabItemId, setActiveTabItemId] = useState<TabItemId>(
    tabIdParam && validTabIdParam.has(tabIdParam) ? tabIdParam : firstTab.id,
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    activeTabItemId,
    setActiveTabItemId,
    tabs,
    firstTab,
    secondTab,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOrdersTabsParams = {
  tabIdParam?: OrderTabId;
  isInStoreOrdersEnabled: boolean;
};

type OrderTabId = 'orders' | 'gift-cards' | 'digital-orders' | 'in-store-orders';
